import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown } from "lucide-react";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CURRENCY_META } from "@/lib/api";
import { PromoNudge } from "@/components/PromoNudge";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [bets, setBets] = useState([]);
  const [txs, setTxs] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("bets");
  const [vip, setVip] = useState(null);

  const load = useCallback(async () => {
    const [b, t, s] = await Promise.all([
      api.get("/bets/me?limit=50"),
      api.get("/transactions/me?limit=50"),
      api.get("/stats/me"),
    ]);
    setBets(b.data);
    setTxs(t.data);
    setStats(s.data);
    api.get("/vip/me").then((r) => setVip(r.data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = [
    { label: "Total Wagered", val: stats?.volume || 0, accent: "#FFC107" },
    { label: "Net Profit", val: stats?.profit || 0, accent: (stats?.profit || 0) >= 0 ? "#00E676" : "#FF3366" },
    { label: "Bets Played", val: stats?.bets || 0, accent: "#00F0FF", isInt: true },
    { label: "Biggest Win", val: stats?.biggest_win || 0, accent: "#D97706" },
  ];

  return (
    <div className="flex flex-col gap-8 fade-up">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Dashboard</h1>
        <div className="text-zinc-500 text-sm mt-1 flex items-center gap-3 flex-wrap">
          <span>Playing as <span className="text-white">{user?.username}</span></span>
          {vip && (
            <Link to="/vip" className="chip flex items-center gap-1.5 hover:bg-white/[0.06]" style={{ color: vip.tier.color }} data-testid="dashboard-vip-chip">
              <Crown size={12} /> {vip.tier.name} · {Math.round(vip.tier.rakeback * 100)}% rakeback
            </Link>
          )}
        </div>
      </div>

      <PromoNudge />

      {/* KPIs */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="panel p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k.label}</div>
            <div className="font-mono-num text-2xl md:text-3xl font-bold mt-2" style={{ color: k.accent }}>
              {k.isInt ? k.val : formatAmount(k.val, "FMI")}
            </div>
          </div>
        ))}
      </section>

      {/* Balances */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display text-xl font-black tracking-tighter">Balances</h2>
          <button onClick={async () => { await refresh(); load(); }} className="text-xs text-zinc-500 hover:text-amber-400 uppercase tracking-widest">
            Refresh
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.keys(CURRENCY_META).map((c) => [c, user?.balances?.[c] ?? 0]).map(([c, v]) => (
            <div key={c} className="panel p-4" data-testid={`balance-${c}`}>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ background: CURRENCY_META[c]?.color }} />
                <span className="text-xs uppercase tracking-wider text-zinc-500">{c}</span>
              </div>
              <div className="font-mono-num text-lg font-semibold mt-2">{formatAmount(v, c)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Tabs */}
      <section>
        <div className="flex items-center gap-1 mb-4 p-1 panel inline-flex">
          {[
            ["bets", "Bet History"],
            ["txs", "Transactions"],
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`text-xs uppercase tracking-wider py-2 px-5 rounded-lg ${
                tab === k ? "bg-white/[0.08] text-white" : "text-zinc-500 hover:text-white"
              }`}
              data-testid={`tab-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        <div className="panel overflow-hidden">
          {tab === "bets" ? (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="bets-table">
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Time</th>
                    <th>Bet</th>
                    <th>Mult.</th>
                    <th>Payout</th>
                    <th>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {bets.length === 0 ? (
                    <tr><td colSpan={6} className="text-zinc-600 text-center py-10">No bets yet. Pick a game.</td></tr>
                  ) : (
                    bets.map((b) => (
                      <tr key={b.id}>
                        <td className="uppercase tracking-wider text-xs">{b.game}</td>
                        <td className="text-zinc-500 font-mono-num text-xs">{new Date(b.created_at).toLocaleTimeString()}</td>
                        <td className="font-mono-num">{formatAmount(b.bet, b.currency)} <span className="text-zinc-500">{b.currency}</span></td>
                        <td className="font-mono-num">×{Number(b.multiplier).toFixed(2)}</td>
                        <td className="font-mono-num">{formatAmount(b.payout, b.currency)}</td>
                        <td className={`font-mono-num font-semibold ${b.profit > 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          {b.profit > 0 ? "+" : ""}{formatAmount(b.profit, b.currency)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="txs-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Time</th>
                    <th>Currency</th>
                    <th>Amount</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.length === 0 ? (
                    <tr><td colSpan={5} className="text-zinc-600 text-center py-10">No transactions yet.</td></tr>
                  ) : (
                    txs.map((t) => (
                      <tr key={t.id}>
                        <td className="uppercase tracking-wider text-xs">{t.type}</td>
                        <td className="text-zinc-500 font-mono-num text-xs">{new Date(t.created_at).toLocaleTimeString()}</td>
                        <td>{t.currency}</td>
                        <td className={`font-mono-num ${t.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          {t.amount >= 0 ? "+" : ""}{formatAmount(t.amount, t.currency)}
                        </td>
                        <td className="text-zinc-500 text-xs">{t.note || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
